/**
 * Phase 46 — UsmPrefsContext
 *
 * Loads and persists the authenticated employee's preferences:
 *   • active_branch_id            (Feature #6 — sticky working branch)
 *   • inventory_columns           (Feature #8 — column layout)
 *   • inventory_filter_presets    (Feature #9 — saved filter presets)
 *
 * Storage lives in MongoDB via /api/usm/prefs/me so the same layout follows
 * the user across devices (PC, iPad, tablet). We DO NOT use localStorage
 * for these — the user explicitly asked for cross-device sync.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import usmApi from "@/lib/usmApi";
import { useUsmAuth } from "@/contexts/UsmAuthContext";

const UsmPrefsContext = createContext(null);

export function UsmPrefsProvider({ children }) {
  const { employee } = useUsmAuth();
  const [prefs, setPrefs] = useState({
    active_branch_id: null,
    inventory_columns: [],
    inventory_filter_presets: [],
  });
  const [ready, setReady] = useState(false);

  // Load on mount / after login
  useEffect(() => {
    if (!employee) { setReady(false); return; }
    let alive = true;
    (async () => {
      try {
        const r = await usmApi.get("/prefs/me");
        if (!alive) return;
        setPrefs({
          active_branch_id: r.data?.active_branch_id || employee.branch_id || null,
          inventory_columns: r.data?.inventory_columns || [],
          inventory_filter_presets: r.data?.inventory_filter_presets || [],
        });
      } catch { /* fall back to defaults from user */ }
      finally { if (alive) setReady(true); }
    })();
    return () => { alive = false; };
  }, [employee]);

  // Persist helpers — all patch a subset and update local state on success.
  const setActiveBranch = useCallback(async (branchId) => {
    setPrefs((p) => ({ ...p, active_branch_id: branchId }));
    try { await usmApi.patch("/prefs/me", { active_branch_id: branchId }); }
    catch { /* revert on failure? Keep local — network hiccup shouldn't force logout */ }
  }, []);

  const setInventoryColumns = useCallback(async (columns) => {
    setPrefs((p) => ({ ...p, inventory_columns: columns }));
    try { await usmApi.patch("/prefs/me", { inventory_columns: columns }); }
    catch { /* keep local optimistic state */ }
  }, []);

  const addFilterPreset = useCallback(async (name, filters) => {
    try {
      const r = await usmApi.post("/prefs/me/presets", { name, filters });
      setPrefs((p) => ({ ...p, inventory_filter_presets: [...p.inventory_filter_presets, r.data] }));
      return r.data;
    } catch { return null; }
  }, []);

  const deleteFilterPreset = useCallback(async (presetId) => {
    try {
      await usmApi.delete(`/prefs/me/presets/${presetId}`);
      setPrefs((p) => ({
        ...p,
        inventory_filter_presets: p.inventory_filter_presets.filter((x) => x.id !== presetId),
      }));
    } catch {/* noop */}
  }, []);

  return (
    <UsmPrefsContext.Provider value={{
      prefs, ready,
      setActiveBranch, setInventoryColumns,
      addFilterPreset, deleteFilterPreset,
    }}>
      {children}
    </UsmPrefsContext.Provider>
  );
}

export function useUsmPrefs() {
  const ctx = useContext(UsmPrefsContext);
  if (!ctx) throw new Error("useUsmPrefs must be used inside <UsmPrefsProvider>");
  return ctx;
}
